import { Flex, type FlexProps, Icon, InterfaceStarFilledIcon, Text } from '@grammarly/design-system'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import classes from './UnlockBestWritingUphook.module.scss'

const DEFAULT_TITLE = 'Unlock your best writing'

export interface UnlockBestWritingUphookProps {
  readonly ctaButton: React.ReactNode
  readonly title?: string
  readonly containerClassName?: string
  readonly hideContent?: boolean
  readonly gap?: FlexProps['gap']
  readonly children?: React.ReactNode
}

export const UnlockBestWritingUphook = (props: UnlockBestWritingUphookProps) => {
  const { children, hideContent, ...rest } = props
  const content = children ? children : <DefaultContent {...rest} />

  return (
    <Flex
      className={classNames(
        classes.uphookWrapper,
        hideContent && classes.withHiddenContent,
        props.containerClassName,
      )}
      align="center"
      justify="space-between"
      gap={props.gap}
    >
      <AnimatePresence initial={false}>
        {!hideContent && <AnimatedContent>{content}</AnimatedContent>}
      </AnimatePresence>
      {props.ctaButton}
    </Flex>
  )
}

const AnimatedContent = (props: { children: React.ReactNode }) => {
  const springTransition = {
    type: 'spring' as const,
    mass: 1,
    stiffness: 100,
    damping: 15,
  }

  return (
    <motion.div
      initial={{ width: 0, opacity: 0, transformOrigin: 'right' }}
      animate={{ width: 'auto', opacity: 1, transformOrigin: 'right' }}
      exit={{
        width: 0,
        opacity: 0,
        transformOrigin: 'right',
        transition: { duration: 0.5, ease: 'easeInOut' },
      }}
      transition={springTransition}
    >
      {props.children}
    </motion.div>
  )
}

const DefaultContent = (props: Exclude<UnlockBestWritingUphookProps, 'children'>) => (
  <Flex gap={1} align="center" justify="space-between">
    <Icon size="small" icon={InterfaceStarFilledIcon} variant="pro" accessibilityLabel="" />
    <Text as="span" weight="medium" variant="text-xsmall" className={classes.defaultContentText}>
      {props.title ?? DEFAULT_TITLE}
    </Text>
  </Flex>
)
